import { useContext, useEffect, useMemo, useState, type FunctionComponent } from 'react';
import { isMobile } from 'react-device-detect';
import { identify, track } from '@fff-web/fff-analytics';
import { FFFStorage, useUserContext } from '@fff-web/fff-utilities';
import GlobalNavInternalContext from '../../context/GlobalNavInternalContext';
import { MOBILE_BANNER_VARIATIONS, OPTIMIZELY_FEATURES_FLAGS } from '../../utils/configurations';
import { VARIATIONS } from '../../utils/constants';
import { getAppMobileLink, REACT_APP_MOBILE_BANNER_URL } from '../../utils/dynamicLinks';
import WithABTest from '../WithABTest';
import NewMobileBanner from './NewMobileBanner/NewMobileBanner';
import OldMobileBanner from './OldMobileBanner/OldMobileBanner';
import type { MobileWebBannerProps } from './types';

const TRACK_DEVICE = 'Mobile Web';
const TRACK_EXPERIENCE = 'smart app banner';
const CTA_BUTTON_TEXT = 'Open';
const BLACKLIST = ['reactivate-my-membership', 'customize', 'boosts'];

const MobileWebBanner: FunctionComponent<MobileWebBannerProps> = ({
  experimentName,
  variationName,
  isVariations,
}) => {
  const [isMobileBannerClosed, setIsMobileBannerClosed] = useState(false);
  const { userInfo } = useUserContext();
  const { env, flags } = useContext(GlobalNavInternalContext);
  const {
    downloadMobileAppWebBannerFF: downloadMobileAppBanner,
    mobileAppWebBannerUniversalLinksFF: mobileAppWebBannerUniversalLinks,
    mobileAppWebBannerLoyaltyProgramFF: mobileAppWebBannerLoyaltyProgram,
  } = flags;
  const isLoggedIn = Boolean(userInfo?.loggedIn);
  const appMobileLink = getAppMobileLink(env, mobileAppWebBannerUniversalLinks);
  const showABTest = variationName === VARIATIONS.ON || isVariations;

  const pathname = useMemo(() => window?.location?.pathname || '', []);

  const isOnBlackList = useMemo(
    () => BLACKLIST.some((path) => pathname.includes(path)),
    [pathname],
  );
  const isMobileAppWebView = useMemo(() => {
    if (typeof navigator === 'undefined' || !navigator.userAgent) {
      return false;
    }

    return navigator.userAgent === 'Fabfitfun Mobile App Webview';
  }, []);

  const showMobileBanner =
    !isMobileBannerClosed &&
    downloadMobileAppBanner &&
    isMobile &&
    !isMobileAppWebView &&
    !isOnBlackList &&
    showABTest &&
    isLoggedIn;

  const handleBannerClose = (closeBanner: boolean, key: string) => {
    FFFStorage.setToStorage({ key, value: true });
    setIsMobileBannerClosed(closeBanner);
    track({
      trackName: 'Web Banner Closed',
      properties: {
        cta_name: 'x',
        experience: TRACK_EXPERIENCE,
        invite_type: TRACK_DEVICE,
      },
    });
  };

  const handleBannerOpen = () => {
    if (!appMobileLink.includes(REACT_APP_MOBILE_BANNER_URL)) {
      window.location.href = appMobileLink;
    }
    track({
      trackName: 'App Download Started',
      properties: {
        cta_name: CTA_BUTTON_TEXT,
        user_id: userInfo?.user?.userId,
        experience: TRACK_EXPERIENCE,
        invite_type: TRACK_DEVICE,
        position_location: appMobileLink,
      },
    });
  };

  const bannerProps = {
    onBannerClick: handleBannerOpen,
    onBannerClose: handleBannerClose,
    appMobileLink,
  };

  useEffect(() => {
    if (!experimentName || !variationName || !showMobileBanner) return;

    identify({
      traits: {
        featureFlagName: OPTIMIZELY_FEATURES_FLAGS.SMART_BANNER_DOWNLOAD,
        experimentName,
        variationName,
      },
    });
    track({
      trackName: 'Experiment Viewed',
      properties: {
        feature_flag_name: OPTIMIZELY_FEATURES_FLAGS.SMART_BANNER_DOWNLOAD,
        experiment_name: experimentName,
        variation_name: variationName,
      },
    });
  }, [experimentName, variationName, showMobileBanner]);

  if (!showMobileBanner) return null;

  return (
    <>
      {variationName === MOBILE_BANNER_VARIATIONS.VERSION_2 && (
        <NewMobileBanner {...bannerProps} showLoyaltyLayout={!!mobileAppWebBannerLoyaltyProgram} />
      )}
      <OldMobileBanner {...bannerProps} />
    </>
  );
};

export default WithABTest(OPTIMIZELY_FEATURES_FLAGS.SMART_BANNER_DOWNLOAD, MobileWebBanner, [
  MOBILE_BANNER_VARIATIONS.VERSION_1,
  MOBILE_BANNER_VARIATIONS.VERSION_2,
]);
